import { Building2, CreditCard } from 'lucide-react';
import { useOrganisationCredits } from '../hooks/useApi';
import { DataTable } from '../components/shared/DataTable';
import { StatusBadge } from '../components/shared/StatusBadge';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import type { OrganisationCredits } from '../types/api';

export function OrganisationsPage() {
  const { data, isLoading, error, refetch } = useOrganisationCredits();

  const columns = [
    { key: 'name', header: 'Organizacja', sortable: true },
    {
      key: 'subscriptionPlan',
      header: 'Plan',
      sortable: true,
      render: (row: OrganisationCredits) => {
        const plan = row.subscriptionPlan;
        if (!plan) return <StatusBadge label="Brak" variant="neutral" />;
        const variant = plan === 'Professional' ? 'info' : plan === 'Trial' ? 'warning' : plan === 'Event' ? 'success' : 'neutral';
        return <StatusBadge label={plan} variant={variant} />;
      },
    },
    {
      key: 'credentialsRemaining',
      header: 'Limit',
      sortable: true,
      render: (row: OrganisationCredits) =>
        row.credentialsRemaining === null ? (
          <span className="text-gray-500 dark:text-gray-400">Bez limitu</span>
        ) : (
          <span className="text-gray-900 dark:text-white font-medium">{row.credentialsRemaining.toLocaleString('pl-PL')}</span>
        ),
    },
    {
      key: 'credentialsReserved',
      header: 'Zarezerwowane',
      sortable: true,
      render: (row: OrganisationCredits) =>
        row.credentialsReserved > 0 ? <span className="text-amber-600 dark:text-amber-300">{row.credentialsReserved}</span> : <span className="text-gray-400 dark:text-gray-600">0</span>,
    },
    {
      key: 'available',
      header: 'Dostepne',
      sortable: true,
      render: (row: OrganisationCredits) => (
        <span className={row.available === 'Unlimited' ? 'text-gray-500 dark:text-gray-400' : 'text-emerald-600 dark:text-emerald-300 font-semibold'}>
          {row.available === 'Unlimited' ? 'Bez limitu' : Number(row.available).toLocaleString('pl-PL')}
        </span>
      ),
    },
    {
      key: 'subscriptionValidUntilDate',
      header: 'Waznosc',
      sortable: true,
      render: (row: OrganisationCredits) => {
        if (!row.subscriptionValidUntilDate) return <span className="text-gray-400 dark:text-gray-600">-</span>;
        const date = new Date(row.subscriptionValidUntilDate);
        const isExpired = date < new Date();
        return (
          <span className={isExpired ? 'text-red-600 dark:text-red-300' : 'text-gray-700 dark:text-gray-300'}>
            {date.toLocaleDateString('pl-PL')}
          </span>
        );
      },
    },
    {
      key: 'hasOwnSeal',
      header: 'Pieczec',
      render: (row: OrganisationCredits) =>
        row.hasOwnSeal ? <StatusBadge label="Wlasna" variant="info" /> : <StatusBadge label="Globalna" variant="neutral" />,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Building2 className="h-6 w-6 text-brand-600 dark:text-brand-400" />
          Organizacje
        </h1>
        <p className="text-sm text-gray-500 mt-1">Kredyty i subskrypcje organizacji</p>
      </div>

      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Kredyty organizacji</h2>
        </div>
        {isLoading ? (
          <LoadingSpinner size="sm" />
        ) : error ? (
          <ErrorAlert message={error.message} onRetry={() => refetch()} />
        ) : data ? (
          <DataTable columns={columns} data={data} compact />
        ) : null}
      </div>
    </div>
  );
}
